import React, { createContext, useContext, useState, useCallback } from "react";
import * as assetService from "../services/assetService";

const AssetContext = createContext();

export const AssetProvider = ({ children }) => {
  const [assets, setAssets] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    page: 1,
    totalPages: 0,
  });

  const fetchAssets = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await assetService.getAllAssets(filters);
      setAssets(response.data || []);
      if (response.pagination) {
        setPagination(response.pagination);
      }
      return response;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch assets");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAssetTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await assetService.getAllAssetTypes();
      setAssetTypes(response.data);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch asset types");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createNewAsset = useCallback(async (assetData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await assetService.createAsset(assetData);
      setAssets((prev) => [response, ...prev]);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create asset");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateExistingAsset = useCallback(async (id, assetData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await assetService.updateAsset(id, assetData);
      setAssets((prev) => prev.map((asset) => (asset.id === id ? response : asset)));
      return response;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update asset");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteExistingAsset = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await assetService.deleteAsset(id);
      setAssets((prev) => prev.filter((asset) => asset.id !== id));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete asset");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    assets,
    assetTypes,
    loading,
    error,
    pagination,
    fetchAssets,
    fetchAssetTypes,
    createNewAsset,
    updateExistingAsset,
    deleteExistingAsset,
  };

  return <AssetContext.Provider value={value}>{children}</AssetContext.Provider>;
};

export const useAssets = () => {
  const context = useContext(AssetContext);
  if (!context) {
    throw new Error("useAssets must be used within AssetProvider");
  }
  return context;
};
